import { useEffect, useState } from "react";
import type { CurrentUser } from "@/lib/auth";
import {
  ANGEBOTE_BADGE_EVENTS,
  ANFRAGEN_ANSWERS_BADGE_EVENTS,
  BILLING_BADGE_EVENTS,
  fetchNavBadgeCounts,
  MAHNWESEN_BADGE_EVENTS,
  MATERIAL_BADGE_EVENTS,
  KONTAKTE_NUMMERN_BADGE_EVENTS,
  VERANSTALTUNG_BADGE_EVENTS,
  MENTIONS_BADGE_EVENTS,
  MONTAGEPLANUNG_BADGE_EVENTS,
  PROJECT_COMPLETION_BADGE_EVENTS,
  SUPPORT_BADGE_EVENTS,
  type NavBadgeCounts,
} from "@/lib/navBadges";
import { realtimeClient, type RealtimeStatus } from "@/lib/realtimeClient";
import {
  getServerUpdateSnapshot,
  subscribeServerUpdate,
} from "@/features/serverUpdate/serverUpdateStore";

/**
 * Nav-Badge-Zähler als prozessweiter Singleton-Store.
 *
 * Früher startete *jede* `useNavBadges`-Instanz (Header, Sidebar, Bestellwesen,
 * Dokumente-Hub, …) ein eigenes 120s-Interval plus 7 Realtime-Listener mit je
 * eigenem Fallback-Polling. Bei 3–4 gleichzeitig gemounteten Instanzen ergab
 * das bis zu ~24 vollständige Badge-Refreshes (à ~8–12 API-Requests) pro
 * 120 Sekunden – pro Tab.
 *
 * Jetzt gibt es genau EINEN Store:
 *  - ein einziges Fallback-Polling (120s, pausiert bei verstecktem Tab),
 *  - ein einziger Realtime-Listener (Union aller Badge-Scopes/-Events),
 *  - Event-Bursts werden debounced (~2s) und Refreshes haben einen
 *    Mindestabstand (~15s),
 *  - Single-Flight: nie zwei parallele `fetchNavBadgeCounts`-Läufe,
 *  - beim Zurückkehren in den Tab wird höchstens einmal aufgefrischt.
 *  - schlägt ein Lauf fehl (Update, API kurz weg), bleiben vorhandene Zähler
 *    stehen und es wird schnell nachgeladen – auch sobald das Update vorbei
 *    ist oder die Live-Verbindung wieder steht.
 *
 * Die öffentliche Hook-API ist unverändert.
 */

const FALLBACK_INTERVAL_MS = 120_000;
const EVENT_DEBOUNCE_MS = 2_000;
const MIN_REFRESH_GAP_MS = 15_000;
const FAILURE_RETRY_MAX = 5;

const REALTIME_SCOPES = [
  "domain:timesheets",
  "timesheets:list",
  "badge:billing",
  "domain:auftraege",
  "domain:projects",
  "auftraege:list",
  "projects:list",
  "badge:project-completions",
  "domain:support",
  "support:list",
  "badge:support",
  "domain:material_anforderungen",
  "material-anforderungen:list",
  "orders:list",
  "badge:material",
  "domain:angebotsanfragen",
  "angebotsanfragen:list",
  "badge:angebote",
  "domain:anfragen",
  "anfragen:list",
  "badge:anfragen-answers",
  "domain:dokumente",
  "dokumente:list",
  "badge:mahnwesen",
  "domain:mentions",
  "mentions:inbox",
  "badge:mentions",
];

const REALTIME_DOMAINS = [
  "timesheets",
  "auftraege",
  "projects",
  "support",
  "material_anforderungen",
  "material-anforderungen",
  "orders",
  "angebotsanfragen",
  "anfragen",
  "dokumente",
  "hero",
  "mentions",
];

const REALTIME_EVENTS = [
  ...new Set<string>([
    ...BILLING_BADGE_EVENTS,
    ...PROJECT_COMPLETION_BADGE_EVENTS,
    ...SUPPORT_BADGE_EVENTS,
    ...MATERIAL_BADGE_EVENTS,
    ...ANGEBOTE_BADGE_EVENTS,
    ...ANFRAGEN_ANSWERS_BADGE_EVENTS,
    ...MAHNWESEN_BADGE_EVENTS,
    ...MENTIONS_BADGE_EVENTS,
  ]),
];

const WINDOW_EVENTS = [
  ...new Set<string>([
    ...MONTAGEPLANUNG_BADGE_EVENTS,
    ...ANFRAGEN_ANSWERS_BADGE_EVENTS,
    ...MATERIAL_BADGE_EVENTS,
    ...MAHNWESEN_BADGE_EVENTS,
    ...MENTIONS_BADGE_EVENTS,
    ...KONTAKTE_NUMMERN_BADGE_EVENTS,
    ...VERANSTALTUNG_BADGE_EVENTS,
  ]),
];

function userKeyOf(user: CurrentUser | null): string {
  if (!user) return "";
  return String(user.login || user.sub || user.username || "")
    .trim()
    .toLowerCase();
}

type Subscriber = (counts: NavBadgeCounts) => void;

class NavBadgeStore {
  private counts: NavBadgeCounts = {};
  private subscribers = new Map<Subscriber, CurrentUser | null>();
  private user: CurrentUser | null = null;
  private userKey = "";

  private unregisterRealtime: (() => void) | null = null;
  private windowHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;

  private inflight: Promise<void> | null = null;
  private scheduledTimer: number | null = null;
  private failureRetryTimer: number | null = null;
  private failureAttempt = 0;
  private lastRefreshStartedAt = 0;
  private staleWhileHidden = false;
  private seenRealtimeOpen = false;
  private realtimeDropped = false;
  private serverUpdateActive = false;
  private unsubscribeServerUpdate: (() => void) | null = null;
  private unsubscribeRealtimeStatus: (() => void) | null = null;

  getSnapshot(): NavBadgeCounts {
    return this.counts;
  }

  subscribe(fn: Subscriber, user: CurrentUser | null): () => void {
    this.subscribers.set(fn, user);
    this.reconcile();
    // Aktuellen Stand sofort liefern (kein Warten auf nächsten Tick).
    fn(this.counts);
    return () => {
      this.subscribers.delete(fn);
      this.reconcile();
    };
  }

  /** Leitet den aktiven Benutzer aus allen Subscribern ab und startet/stoppt. */
  private reconcile(): void {
    let nextUser: CurrentUser | null = null;
    for (const candidate of this.subscribers.values()) {
      if (userKeyOf(candidate)) {
        nextUser = candidate;
        break;
      }
    }
    const nextKey = userKeyOf(nextUser);

    if (!nextKey) {
      // Kein Subscriber mit Benutzer (alle unmounted oder abgemeldet).
      if (this.userKey || this.unregisterRealtime) {
        this.userKey = "";
        this.user = null;
        this.lastRefreshStartedAt = 0;
        this.counts = {};
        this.emit();
        this.stop();
      }
      return;
    }

    // Neuestes User-Objekt merken (Rollen können sich z. B. per
    // Dev-Role-Switch ändern, auch wenn der Login gleich bleibt).
    this.user = nextUser;
    if (nextKey !== this.userKey) {
      this.userKey = nextKey;
      this.failureAttempt = 0;
      this.clearFailureRetry();
      this.counts = {};
      this.emit();
      this.start();
      void this.refresh();
    } else {
      this.start();
      if (!this.lastRefreshStartedAt) void this.refresh();
    }
  }

  private start(): void {
    if (typeof window === "undefined") return;
    if (!this.unregisterRealtime) {
      // Ein einziger Realtime-Listener; dessen Fallback-Polling (pausiert
      // automatisch bei verstecktem Tab) ist zugleich unser einziges Interval.
      this.unregisterRealtime = realtimeClient.addListener({
        scopes: REALTIME_SCOPES,
        domains: REALTIME_DOMAINS,
        events: REALTIME_EVENTS,
        fallbackIntervalMs: FALLBACK_INTERVAL_MS,
        onChange: () => this.scheduleRefresh(),
      });
    }
    if (!this.windowHandler) {
      this.windowHandler = () => this.scheduleRefresh();
      for (const eventName of WINDOW_EVENTS) {
        window.addEventListener(eventName, this.windowHandler);
      }
    }
    if (!this.visibilityHandler) {
      this.visibilityHandler = () => {
        if (document.visibilityState !== "visible") return;
        // Nach Rückkehr in den Tab höchstens einmal auffrischen, wenn die
        // Daten inzwischen veraltet sind.
        const stale =
          this.staleWhileHidden ||
          Date.now() - this.lastRefreshStartedAt >= FALLBACK_INTERVAL_MS;
        if (stale) {
          this.staleWhileHidden = false;
          this.scheduleRefresh();
        }
      };
      document.addEventListener("visibilitychange", this.visibilityHandler);
    }
    if (!this.unsubscribeServerUpdate) {
      this.serverUpdateActive = getServerUpdateSnapshot().active;
      this.unsubscribeServerUpdate = subscribeServerUpdate(() => {
        const active = getServerUpdateSnapshot().active;
        const finished = this.serverUpdateActive && !active;
        this.serverUpdateActive = active;
        if (finished) this.forceRefresh();
      });
    }
    if (!this.unsubscribeRealtimeStatus) {
      this.unsubscribeRealtimeStatus = realtimeClient.onStatus((status) => {
        this.onRealtimeStatus(status);
      });
    }
  }

  /** Live-Verbindung war weg (Deploy) und steht wieder: Zähler sofort nachziehen. */
  private onRealtimeStatus(status: RealtimeStatus): void {
    if (status === "open") {
      if (this.realtimeDropped) {
        this.realtimeDropped = false;
        this.forceRefresh();
      }
      this.seenRealtimeOpen = true;
      return;
    }
    if (this.seenRealtimeOpen) this.realtimeDropped = true;
  }

  private stop(): void {
    if (this.unregisterRealtime) {
      this.unregisterRealtime();
      this.unregisterRealtime = null;
    }
    if (this.windowHandler) {
      for (const eventName of WINDOW_EVENTS) {
        window.removeEventListener(eventName, this.windowHandler);
      }
      this.windowHandler = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.scheduledTimer != null) {
      window.clearTimeout(this.scheduledTimer);
      this.scheduledTimer = null;
    }
    this.clearFailureRetry();
    if (this.unsubscribeServerUpdate) {
      this.unsubscribeServerUpdate();
      this.unsubscribeServerUpdate = null;
    }
    if (this.unsubscribeRealtimeStatus) {
      this.unsubscribeRealtimeStatus();
      this.unsubscribeRealtimeStatus = null;
    }
    this.seenRealtimeOpen = false;
    this.realtimeDropped = false;
  }

  private clearFailureRetry(): void {
    if (this.failureRetryTimer != null) {
      window.clearTimeout(this.failureRetryTimer);
      this.failureRetryTimer = null;
    }
  }

  /** Nach einem fehlgeschlagenen Lauf zügig erneut versuchen (Update-Fenster). */
  private scheduleFailureRetry(): void {
    if (!this.user || this.failureRetryTimer != null) return;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      this.staleWhileHidden = true;
      return;
    }
    this.failureAttempt += 1;
    if (this.failureAttempt > FAILURE_RETRY_MAX) return;
    const delay = Math.min(30_000, 2_000 * 2 ** (this.failureAttempt - 1));
    this.failureRetryTimer = window.setTimeout(() => {
      this.failureRetryTimer = null;
      void this.refresh();
    }, delay);
  }

  /** Mindestabstand überspringen: Update vorbei oder Verbindung wieder da. */
  private forceRefresh(): void {
    if (!this.user) return;
    this.clearFailureRetry();
    this.failureAttempt = 0;
    if (this.scheduledTimer != null) {
      window.clearTimeout(this.scheduledTimer);
      this.scheduledTimer = null;
    }
    const run = () => {
      this.lastRefreshStartedAt = 0;
      void this.refresh();
    };
    if (this.inflight) {
      void this.inflight.finally(run);
      return;
    }
    run();
  }

  private emit(): void {
    for (const fn of this.subscribers.keys()) {
      try {
        fn(this.counts);
      } catch {
        /* Subscriber-Fehler dürfen andere nicht blockieren */
      }
    }
  }

  /** Debounced + Mindestabstand: Event-Bursts kollabieren zu einem Refresh. */
  private scheduleRefresh(): void {
    if (!this.user) return;
    if (this.scheduledTimer != null) return; // bereits geplant → koaleszieren
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      this.staleWhileHidden = true;
      return;
    }
    const now = Date.now();
    const earliest = Math.max(
      this.lastRefreshStartedAt + MIN_REFRESH_GAP_MS,
      now + EVENT_DEBOUNCE_MS,
    );
    const delay = Math.max(0, earliest - now);
    this.scheduledTimer = window.setTimeout(() => {
      this.scheduledTimer = null;
      void this.refresh();
    }, delay);
  }

  private refresh(): Promise<void> {
    const user = this.user;
    if (!user) return Promise.resolve();
    if (this.inflight) return this.inflight;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      this.staleWhileHidden = true;
      return Promise.resolve();
    }
    const keyAtStart = this.userKey;
    this.lastRefreshStartedAt = Date.now();
    this.inflight = fetchNavBadgeCounts(user)
      .then((result) => {
        if (this.userKey !== keyAtStart) return;
        if (result.failed > 0) {
          // Erfolgreiche Zähler übernehmen, bereits geladene bei Fehlern behalten.
          this.counts = { ...this.counts, ...result.counts };
          this.emit();
          this.scheduleFailureRetry();
          return;
        }
        this.failureAttempt = 0;
        this.clearFailureRetry();
        this.counts = result.counts;
        this.emit();
      })
      .catch(() => {
        // Badge-Fehler sollen die Navigation nicht blockieren.
        if (this.userKey === keyAtStart) this.scheduleFailureRetry();
      })
      .finally(() => {
        this.inflight = null;
      });
    return this.inflight;
  }
}

const navBadgeStore = new NavBadgeStore();

export function useNavBadges(user: CurrentUser | null): NavBadgeCounts {
  const [counts, setCounts] = useState<NavBadgeCounts>(() => navBadgeStore.getSnapshot());
  const userKey = userKeyOf(user);

  useEffect(() => {
    return navBadgeStore.subscribe(setCounts, user);
    // Bewusst nur der stabile Key: `user`-Objekte wechseln die Identität bei
    // jedem /me-Refresh, der Store soll aber nur bei echtem Benutzerwechsel
    // neu starten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey]);

  return counts;
}
